import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  Building2,
  FileText,
  CreditCard,
  CheckCircle,
  ArrowRight,
  ArrowLeft,
  Loader2,
  Sparkles,
  MapPin,
  Phone,
  Mail,
  Globe,
  Receipt,
  Shield,
} from "lucide-react";

const REGIMEN_FISCAL_OPTIONS = [
  { value: "601", label: "601 - General de Ley Personas Morales" },
  { value: "603", label: "603 - Personas Morales con Fines no Lucrativos" },
  { value: "605", label: "605 - Sueldos y Salarios" },
  { value: "606", label: "606 - Arrendamiento" },
  { value: "607", label: "607 - Régimen de Enajenación o Adquisición de Bienes" },
  { value: "608", label: "608 - Demás ingresos" },
  { value: "610", label: "610 - Residentes en el Extranjero" },
  { value: "611", label: "611 - Ingresos por Dividendos" },
  { value: "612", label: "612 - Personas Físicas con Actividades Empresariales y Profesionales" },
  { value: "614", label: "614 - Ingresos por intereses" },
  { value: "615", label: "615 - Régimen de los ingresos por obtención de premios" },
  { value: "616", label: "616 - Sin obligaciones fiscales" },
  { value: "620", label: "620 - Sociedades Cooperativas de Producción" },
  { value: "621", label: "621 - Incorporación Fiscal" },
  { value: "622", label: "622 - Actividades Agrícolas, Ganaderas, Silvícolas y Pesqueras" },
  { value: "623", label: "623 - Opcional para Grupos de Sociedades" },
  { value: "624", label: "624 - Coordinados" },
  { value: "625", label: "625 - Régimen de las Actividades Empresariales con ingresos a través de Plataformas Tecnológicas" },
  { value: "626", label: "626 - Régimen Simplificado de Confianza" },
];

const SUBSCRIPTION_PLANS = [
  {
    id: "base",
    name: "Plan Sin Facturación",
    price: 2500,
    description: "Acceso completo al sistema para gestión empresarial",
    features: [
      "Gestión de clientes y proveedores",
      "Cotizaciones ilimitadas",
      "Control de proyectos",
      "Órdenes de compra",
      "Reportes básicos",
      "Soporte por email",
    ],
    recommended: false,
  },
  {
    id: "with_billing",
    name: "Plan Con Facturación",
    price: 3000,
    description: "Todo incluido + Facturación Electrónica CFDI 4.0",
    features: [
      "Todo del plan sin facturación",
      "Facturación Electrónica CFDI 4.0",
      "Timbrado ilimitado",
      "Notas de crédito",
      "Complementos de pago",
      "Soporte prioritario",
    ],
    recommended: true,
  },
];

const SetupWizard = () => {
  const { api, company, refreshCompany } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Form data for each step
  const [generalData, setGeneralData] = useState({
    business_name: "",
    trade_name: "",
    address: "",
    city: "",
    state: "",
    postal_code: "",
    phone: "",
    email: "",
    website: "",
  });
  
  const [fiscalData, setFiscalData] = useState({
    rfc: "",
    regimen_fiscal: "",
    codigo_postal_fiscal: "",
  });
  
  const [selectedPlan, setSelectedPlan] = useState("");

  // Load existing company data
  useEffect(() => {
    if (company) {
      setGeneralData({
        business_name: company.business_name || "",
        trade_name: company.trade_name || "",
        address: company.address || "",
        city: company.city || "",
        state: company.state || "",
        postal_code: company.postal_code || "",
        phone: company.phone || "",
        email: company.email || "",
        website: company.website || "",
      });
      setFiscalData({
        rfc: company.rfc || "",
        regimen_fiscal: company.regimen_fiscal || "",
        codigo_postal_fiscal: company.codigo_postal_fiscal || "",
      });
      setSelectedPlan(company.subscription_plan || "");
      
      // Determine starting step based on what's already filled
      if (!company.business_name || !company.rfc) {
        setCurrentStep(1);
      } else if (!company.regimen_fiscal || !company.codigo_postal_fiscal) {
        setCurrentStep(2);
      } else if (!company.subscription_plan || company.subscription_status !== "active") {
        setCurrentStep(3);
      } else {
        // All setup complete, redirect to dashboard
        navigate("/dashboard");
      }
    }
  }, [company, navigate]);

  const totalSteps = 3;
  const progress = (currentStep / totalSteps) * 100;

  const handleSaveGeneralData = async () => {
    if (!generalData.business_name) {
      toast.error("El nombre de la empresa es requerido");
      return false;
    }
    
    setSaving(true);
    try {
      await api.patch("/companies/current", generalData);
      await refreshCompany();
      toast.success("Datos generales guardados");
      return true;
    } catch (error) {
      toast.error("Error al guardar datos generales");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSaveFiscalData = async () => {
    if (!fiscalData.rfc || fiscalData.rfc.length < 12) {
      toast.error("El RFC debe tener al menos 12 caracteres");
      return false;
    }
    if (!fiscalData.regimen_fiscal) {
      toast.error("Selecciona un régimen fiscal");
      return false;
    }
    if (!fiscalData.codigo_postal_fiscal || fiscalData.codigo_postal_fiscal.length !== 5) {
      toast.error("El código postal fiscal debe tener 5 dígitos");
      return false;
    }
    
    setSaving(true);
    try {
      await api.patch("/companies/current", fiscalData);
      await refreshCompany();
      toast.success("Datos fiscales guardados");
      return true;
    } catch (error) {
      toast.error("Error al guardar datos fiscales");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSelectPlan = async (planId) => {
    setSelectedPlan(planId);
  };

  const handleNext = async () => {
    if (currentStep === 1) {
      const success = await handleSaveGeneralData();
      if (success) setCurrentStep(2);
    } else if (currentStep === 2) {
      const success = await handleSaveFiscalData();
      if (success) setCurrentStep(3);
    } else if (currentStep === 3) {
      if (!selectedPlan) {
        toast.error("Selecciona un plan de suscripción");
        return;
      }
      // Navigate to subscription page to complete payment
      navigate(`/subscription?plan=${selectedPlan}`);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkipToSubscription = () => {
    setCurrentStep(3);
  };

  const getStepIcon = (step) => {
    const icons = {
      1: Building2,
      2: FileText,
      3: CreditCard,
    };
    return icons[step];
  };

  const getStepTitle = (step) => {
    const titles = {
      1: "Datos Generales",
      2: "Datos Fiscales",
      3: "Suscripción",
    };
    return titles[step];
  };

  const isStepComplete = (step) => {
    if (step === 1) {
      return !!generalData.business_name;
    }
    if (step === 2) {
      return !!fiscalData.rfc && !!fiscalData.regimen_fiscal && !!fiscalData.codigo_postal_fiscal;
    }
    if (step === 3) {
      return !!selectedPlan;
    }
    return false;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <div className="container max-w-4xl mx-auto py-8 px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Sparkles className="h-4 w-4" />
            Configuración Inicial
          </div>
          <h1 className="text-3xl font-bold text-slate-800 mb-2">
            ¡Bienvenido a CIA Servicios®!
          </h1>
          <p className="text-slate-600">
            Completa estos pasos para comenzar a usar el sistema
          </p>
        </div>

        {/* Progress */}
        <div className="mb-8">
          <div className="flex justify-between mb-2">
            {[1, 2, 3].map((step) => {
              const Icon = getStepIcon(step);
              const isActive = currentStep === step;
              const isComplete = step < currentStep || isStepComplete(step);
              
              return (
                <div
                  key={step}
                  className={`flex items-center gap-2 ${
                    isActive ? "text-blue-600" : isComplete ? "text-green-600" : "text-slate-400"
                  }`}
                >
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${
                      isActive
                        ? "border-blue-600 bg-blue-50"
                        : isComplete
                        ? "border-green-500 bg-green-50"
                        : "border-slate-300 bg-white"
                    }`}
                  >
                    {isComplete && step < currentStep ? (
                      <CheckCircle className="h-5 w-5 text-green-500" />
                    ) : (
                      <Icon className="h-5 w-5" />
                    )}
                  </div>
                  <span className="text-sm font-medium hidden sm:inline">
                    {getStepTitle(step)}
                  </span>
                </div>
              );
            })}
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Step Content */}
        <Card className="shadow-lg border-0">
          <CardHeader className="border-b bg-slate-50/50">
            <div className="flex items-center gap-3">
              {React.createElement(getStepIcon(currentStep), {
                className: "h-6 w-6 text-blue-600",
              })}
              <div>
                <CardTitle>Paso {currentStep}: {getStepTitle(currentStep)}</CardTitle>
                <CardDescription>
                  {currentStep === 1 && "Ingresa la información básica de tu empresa"}
                  {currentStep === 2 && "Configura tus datos fiscales para facturación"}
                  {currentStep === 3 && "Elige el plan que mejor se adapte a tus necesidades"}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            {/* Step 1: General Data */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label htmlFor="business_name" className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-slate-500" />
                      Razón Social *
                    </Label>
                    <Input
                      id="business_name"
                      value={generalData.business_name}
                      onChange={(e) => setGeneralData({ ...generalData, business_name: e.target.value.toUpperCase() })}
                      placeholder="EMPRESA S.A. DE C.V."
                      className="mt-1"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="trade_name">Nombre Comercial</Label>
                    <Input
                      id="trade_name"
                      value={generalData.trade_name}
                      onChange={(e) => setGeneralData({ ...generalData, trade_name: e.target.value })}
                      placeholder="Mi Empresa"
                      className="mt-1"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="phone" className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-slate-500" />
                      Teléfono
                    </Label>
                    <Input
                      id="phone"
                      value={generalData.phone}
                      onChange={(e) => setGeneralData({ ...generalData, phone: e.target.value })}
                      placeholder="33 1234 5678"
                      className="mt-1"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="email" className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-slate-500" />
                      Correo Electrónico
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={generalData.email}
                      onChange={(e) => setGeneralData({ ...generalData, email: e.target.value })}
                      placeholder="contacto@empresa.com"
                      className="mt-1"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="website" className="flex items-center gap-2">
                      <Globe className="h-4 w-4 text-slate-500" />
                      Sitio Web
                    </Label>
                    <Input
                      id="website"
                      value={generalData.website}
                      onChange={(e) => setGeneralData({ ...generalData, website: e.target.value })}
                      placeholder="www.empresa.com"
                      className="mt-1"
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <Label htmlFor="address" className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-slate-500" />
                      Dirección
                    </Label>
                    <Textarea
                      id="address"
                      value={generalData.address}
                      onChange={(e) => setGeneralData({ ...generalData, address: e.target.value })}
                      placeholder="Calle, Número, Colonia"
                      className="mt-1"
                      rows={2}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="city">Ciudad</Label>
                    <Input
                      id="city"
                      value={generalData.city}
                      onChange={(e) => setGeneralData({ ...generalData, city: e.target.value })}
                      placeholder="Guadalajara"
                      className="mt-1"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="state">Estado</Label>
                    <Input
                      id="state"
                      value={generalData.state}
                      onChange={(e) => setGeneralData({ ...generalData, state: e.target.value })}
                      placeholder="Jalisco"
                      className="mt-1"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="postal_code">Código Postal</Label>
                    <Input
                      id="postal_code"
                      value={generalData.postal_code}
                      onChange={(e) => setGeneralData({ ...generalData, postal_code: e.target.value })}
                      placeholder="44100"
                      maxLength={5}
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Fiscal Data */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <Shield className="h-5 w-5 text-amber-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-800">Importante</p>
                      <p className="text-sm text-amber-700">
                        Estos datos deben coincidir exactamente con tu Constancia de Situación Fiscal del SAT
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label htmlFor="rfc" className="flex items-center gap-2">
                      <Receipt className="h-4 w-4 text-slate-500" />
                      RFC *
                    </Label>
                    <Input
                      id="rfc"
                      value={fiscalData.rfc}
                      onChange={(e) => setFiscalData({ ...fiscalData, rfc: e.target.value.toUpperCase() })}
                      placeholder="XAXX010101000"
                      maxLength={13}
                      className="mt-1 font-mono"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      12 caracteres para personas morales, 13 para personas físicas
                    </p>
                  </div>
                  
                  <div className="md:col-span-2">
                    <Label htmlFor="regimen_fiscal">Régimen Fiscal *</Label>
                    <Select
                      value={fiscalData.regimen_fiscal}
                      onValueChange={(value) => setFiscalData({ ...fiscalData, regimen_fiscal: value })}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Selecciona tu régimen fiscal" />
                      </SelectTrigger>
                      <SelectContent>
                        {REGIMEN_FISCAL_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="codigo_postal_fiscal">Código Postal Fiscal *</Label>
                    <Input
                      id="codigo_postal_fiscal"
                      value={fiscalData.codigo_postal_fiscal}
                      onChange={(e) => setFiscalData({ ...fiscalData, codigo_postal_fiscal: e.target.value })}
                      placeholder="44100"
                      maxLength={5}
                      className="mt-1 font-mono"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Según tu Constancia de Situación Fiscal
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Subscription */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {SUBSCRIPTION_PLANS.map((plan) => (
                    <div
                      key={plan.id}
                      onClick={() => handleSelectPlan(plan.id)}
                      className={`relative p-6 rounded-xl border-2 cursor-pointer transition-all ${
                        selectedPlan === plan.id
                          ? "border-blue-500 bg-blue-50 shadow-lg"
                          : "border-slate-200 hover:border-slate-300 hover:shadow"
                      }`}
                    >
                      {plan.recommended && (
                        <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-blue-600 to-indigo-600">
                          Recomendado
                        </Badge>
                      )}
                      
                      <div className="text-center mb-4">
                        <h3 className="text-xl font-bold text-slate-800">{plan.name}</h3>
                        <div className="mt-2">
                          <span className="text-3xl font-bold text-blue-600">${plan.price.toLocaleString()}</span>
                          <span className="text-slate-500">/mes</span>
                        </div>
                        <p className="text-sm text-slate-600 mt-2">{plan.description}</p>
                      </div>
                      
                      <ul className="space-y-2">
                        {plan.features.map((feature, index) => (
                          <li key={index} className="flex items-center gap-2 text-sm">
                            <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                            <span className="text-slate-700">{feature}</span>
                          </li>
                        ))}
                      </ul>
                      
                      {selectedPlan === plan.id && (
                        <div className="absolute top-4 right-4">
                          <CheckCircle className="h-6 w-6 text-blue-500" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                
                <div className="p-4 bg-slate-50 rounded-lg text-center">
                  <p className="text-sm text-slate-600">
                    Al continuar, serás redirigido a la página de pago para completar tu suscripción.
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-6">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={currentStep === 1 || saving}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Anterior
          </Button>
          
          <div className="flex gap-2">
            {currentStep < 3 && (
              <Button
                variant="ghost"
                onClick={handleSkipToSubscription}
                className="text-slate-500"
              >
                Saltar a suscripción
              </Button>
            )}
            
            <Button
              onClick={handleNext}
              disabled={saving}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : null}
              {currentStep === 3 ? "Continuar al pago" : "Siguiente"}
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SetupWizard;
